import type { AppState } from "./types/index";
import { normalizeRole } from "./utils/formatting";
import { appConfig } from "./config";

type StateKey = keyof AppState;
type Listener = (value: any) => void;

const listeners = new Map<StateKey, Set<Listener>>();

function createDefaultState(): AppState {
  return {
    assets: [],
    forecasts: [],
    rules: [],
    filtered: [],
    dashboardChartDimension: "brand",
    role: normalizeRole(appConfig.defaultRole || "market"),
    editingRuleId: null,
    pendingAccept: null,
    pendingDecision: "yes",
    reviewedMasterIds: new Set(),
    reviewInFlight: false,
    expandedProjects: new Set(),
    expandedParents: new Set(),
    expandedBookingProjects: new Set(),
    expandedBookingParents: new Set(),
    dashboardForecastMode: "brief",
    dashboardHistMode: "count",
    expandedDashboardProjects: new Set(),
    auditLog: [],
    notifications: [],
    unreadCount: 0,
  };
}

let state: AppState = createDefaultState();

/**
 * Returns the current application state (readonly reference).
 */
export function getState(): Readonly<AppState> {
  return state;
}

/**
 * Merge a partial update into the state and notify subscribers
 * for any keys whose values actually changed.
 */
export function updateState(partial: Partial<AppState>): void {
  const changedKeys: StateKey[] = [];

  for (const key of Object.keys(partial) as StateKey[]) {
    const newVal = partial[key];
    if (state[key] !== newVal) {
      (state as Record<string, unknown>)[key] = newVal;
      changedKeys.push(key);
    }
  }

  // Deduplicate listeners across changed keys to avoid calling
  // the same listener multiple times (e.g., a panel's render() that
  // subscribes to both "assets" and "forecasts").
  const uniqueListeners = new Set<Listener>();
  for (const key of changedKeys) {
    const keyListeners = listeners.get(key);
    if (keyListeners) {
      for (const fn of keyListeners) {
        uniqueListeners.add(fn);
      }
    }
  }
  for (const fn of uniqueListeners) {
    fn(undefined);
  }
}

/**
 * Subscribe to changes on a specific state key.
 * Returns an unsubscribe function.
 */
export function subscribe(key: StateKey, listener: Listener): () => void {
  if (!listeners.has(key)) {
    listeners.set(key, new Set());
  }
  listeners.get(key)!.add(listener);

  return () => {
    listeners.get(key)?.delete(listener);
  };
}

/**
 * Reset state to defaults and clear all listeners (for testing).
 */
export function resetState(): void {
  state = createDefaultState();
  listeners.clear();
}
